import { Button, FormSelect, Select } from '@civilgrid/ui/components';
import {
  ELEVATION_LABEL,
  ELLIPSOIDAL_HEIGHT_LABEL,
  ellipsoidalHeightFromCoordinates,
  formatElevationFeet,
  formatElevationValue,
  shouldShowEllipsoidalHeight,
} from '@civilgrid/utils/src/geoItems/elevationDisclosure';
import { formatAccuracyFeet } from '@civilgrid/utils/src/geoItems/unitConversion';
import {
  getUtilityMeasurementEntries,
  type UtilityMeasurementEntry,
} from '@civilgrid/utils/src/geoItems/utilityMeasurements';
import { useParams } from '@tanstack/react-router';
import classNames from 'classnames';
import { type ReactNode, useEffect, useState } from 'react';
import { type Control, type SubmitHandler, useForm } from 'react-hook-form';
import type { GeoItem } from '~/common/types';
import Checkbox from '~/components2/forms/Checkbox';
import ControlledInput from '~/components2/forms/controlled/ControlledInput';
import Form from '~/components2/forms/Form';
import Icon from '~/components2/Icon';
import { Notify } from '~/components2/Notify';
import { usePerUtilityMeasurementsEnabled } from '~/hooks/usePerUtilityMeasurementsEnabled';
import type { ProjectDetailsParams } from '..';
import { accuracyProvenanceFields } from './accuracyProvenanceFields';
import {
  buildPotholeUpdateProps,
  FileList,
  FormSection,
  getInitialValues,
  infEffItems,
  Label,
  LONGITUDE_LATITUDE_PROP_LABEL,
  manholeFieldConfig,
  manholeFieldMap,
  materialItems,
  potholeFieldMap,
  priorityItems,
  ReadOnlyField,
  siteAccessItems,
  siteChoiceItems,
  soilItems,
  structuralIntegrityItems,
  surchargeItems,
  surfaceTypeItems,
  type TManholeFormValues,
  type TPotholeFormValues,
  useDeleteGeoItem,
  useEditBaseVersion,
  useInvalidateGeoItems,
  useUpdateGeoItem,
  utilityTypes,
  weatherConditionItems,
  yesNoItems,
  yesNoMaybeItems,
} from './common';
import { PhNumberAutoNumberButton } from './PhNumberAutoNumberButton';
import { PH_NUMBER_TAKEN, usePhNumberClash } from './usePhNumberClash';

function invertMap(obj: { [key: string]: string }) {
  return Object.fromEntries(Object.entries(obj).map(([k, v]) => [v, k]));
}

const manholeMappings = invertMap(manholeFieldMap);

/** Pothole form fields that make up the legacy Survey Results section. */
const SURVEY_RESULT_FIELDS = [
  'northing',
  'easting',
  'elevation',
  'longitudeLatitude',
  'surveyDate',
  'surveyedBy',
] as const satisfies readonly (keyof TPotholeFormValues)[];

/**
 * Client-side display label for the block at `index`: the utility type, with a
 * 1-based occurrence suffix when more than one block shares that type (e.g. the
 * second "Gas" renders as "Gas (2)"). The first occurrence shows the bare type.
 */
function utilityDisplayLabel(entries: UtilityMeasurementEntry[], index: number): string {
  const type = entries[index]?.utility ?? '';
  let occurrence = 0;
  for (let i = 0; i <= index && i < entries.length; i++) {
    if (entries[i].utility === type) occurrence += 1;
  }
  return occurrence > 1 ? `${type} (${occurrence})` : type;
}

/**
 * Reusable collapsible card shared by every collapsible block in the pothole
 * form (per-utility measurements, Survey Results) so they all share the same
 * header style + bounding box. The header (chevron + title) toggles; an optional
 * `headerRight` slot (e.g. a delete button) shows only while expanded.
 * Collapsing only hides the body — react-hook-form retains field values (no
 * shouldUnregister).
 */
function CollapsibleCard({
  title,
  children,
  defaultCollapsed = false,
  testId,
  headerRight,
}: {
  title: ReactNode;
  children: ReactNode;
  defaultCollapsed?: boolean;
  /** Header button gets `${testId}.toggle`. */
  testId?: string;
  headerRight?: ReactNode;
}) {
  const [collapsed, setCollapsed] = useState(defaultCollapsed);

  return (
    <div className="overflow-hidden rounded border border-gray-200">
      <div
        className={classNames('flex items-center justify-between gap-2 bg-grey-10 px-3 py-2.5', {
          'border-b border-gray-200': !collapsed,
        })}
      >
        <button
          type="button"
          className="flex flex-1 items-center gap-2 text-left text-lg font-medium text-primary"
          aria-expanded={!collapsed}
          onClick={() => setCollapsed((c) => !c)}
          data-testid={testId ? `${testId}.toggle` : undefined}
        >
          <Icon icon={collapsed ? 'chevronDown' : 'chevronUp'} className="size-5" />
          {title}
        </button>
        {!collapsed && headerRight}
      </div>
      {!collapsed && <div className="space-y-2 p-3">{children}</div>}
    </div>
  );
}

/**
 * A single collapsible per-utility measurement block, identified by its array
 * position so duplicate utility types stay independent. Delete shows only while
 * expanded; the block starts expanded so a freshly-added utility is ready for
 * input.
 */
function UtilityMeasurementCard({
  control,
  index,
  displayLabel,
  onDelete,
}: {
  control: Control<TPotholeFormValues>;
  index: number;
  displayLabel: string;
  onDelete: () => void;
}) {
  return (
    <CollapsibleCard
      title={displayLabel}
      testId={`pothole.utilityMeasurement.${index}`}
      headerRight={
        <Button
          type="button"
          variant="danger"
          size="small"
          onClick={onDelete}
          data-testid={`pothole.utilityMeasurement.${index}.delete`}
        >
          Delete
        </Button>
      }
    >
      <ControlledInput control={control} name={`utilityMeasurements.${index}.depthToTop`} label="Depth to Top (in)" />
      <ControlledInput
        control={control}
        name={`utilityMeasurements.${index}.depthToBottom`}
        label="Depth to Bottom (in)"
      />
      <ControlledInput control={control} name={`utilityMeasurements.${index}.depth`} label="Depth (in)" />
      <FormSelect
        control={control}
        name={`utilityMeasurements.${index}.material`}
        label="Material"
        options={materialItems}
        data-testid={`field-note-utility-measurement-${index}-material-select`}
      />
      <ControlledInput control={control} name={`utilityMeasurements.${index}.size`} label="Size (in)" />
      <ControlledInput control={control} name={`utilityMeasurements.${index}.owner`} label="Utility Owner" />
    </CollapsibleCard>
  );
}

/**
 * "Add Utility" affordance. Per design, the button does not open a picker
 * directly — it toggles the visibility of a selection menu, which the user then
 * taps to choose a utility. Picking one adds it and hides the menu again.
 */
function AddUtilityControl({ utilityOptions, onAdd }: { utilityOptions: string[]; onAdd: (utility: string) => void }) {
  const [showSelect, setShowSelect] = useState(false);

  if (utilityOptions.length === 0) return null;

  return (
    <div className="mt-3 flex items-center gap-2">
      <Button
        type="button"
        variant="secondary"
        size="big"
        aria-expanded={showSelect}
        onClick={() => setShowSelect((s) => !s)}
        data-testid="pothole.addUtility"
      >
        <Icon icon="plus" className="size-4" />
        Add Utility
      </Button>
      {showSelect && (
        <Select
          label="Select Utility"
          options={utilityOptions.map((t) => ({ value: t, label: t }))}
          value=""
          placeholder="Select Utility"
          onValueChange={(v) => {
            if (v) {
              onAdd(v);
              setShowSelect(false);
            }
          }}
          data-testid="pothole.addUtilitySelect"
        />
      )}
    </div>
  );
}

/**
 * "Location & Accuracy" rows for a pothole. The GNSS provenance — reference frame, solution type,
 * receiver, capture time — sits behind "See more", mirroring the mobile form: crews read the
 * coordinate and the RMS figures, while the provenance only has to stay reachable for anyone
 * checking where a coordinate came from.
 */
export function LocationAccuracySection({ geoItem }: { geoItem: GeoItem }) {
  const [showMore, setShowMore] = useState(false);
  const { accuracy } = geoItem;
  const coordinates = geoItem.geometry?.coordinates;

  return (
    <FormSection title="Location & Accuracy">
      <ReadOnlyField label="Latitude" value={coordinates?.[1]?.toFixed(8)} />
      <ReadOnlyField label="Longitude" value={coordinates?.[0]?.toFixed(8)} />
      <ReadOnlyField label={ELEVATION_LABEL} value={formatElevationValue(accuracy)} />
      {shouldShowEllipsoidalHeight(accuracy, coordinates) && (
        <ReadOnlyField
          label={ELLIPSOIDAL_HEIGHT_LABEL}
          value={formatElevationFeet(ellipsoidalHeightFromCoordinates(coordinates) as number)}
        />
      )}
      {accuracy && (
        <>
          <ReadOnlyField label="HRMS" value={`${formatAccuracyFeet(accuracy.hrms)} ft`} />
          <ReadOnlyField
            label="VRMS"
            value={accuracy.vrms !== null ? `${formatAccuracyFeet(accuracy.vrms)} ft` : 'N/A'}
          />
          <ReadOnlyField
            label="Antenna Height"
            value={accuracy.antennaHeight != null ? `${formatAccuracyFeet(accuracy.antennaHeight)} ft` : 'Unknown'}
          />
          {showMore &&
            accuracyProvenanceFields(accuracy, coordinates).map((field) => (
              <ReadOnlyField key={field.label} label={field.label} value={field.value} />
            ))}
          <button
            type="button"
            className="flex cursor-pointer items-center gap-1 pt-1 text-sm text-gray-500 hover:text-primary"
            aria-expanded={showMore}
            onClick={() => setShowMore((open) => !open)}
            data-testid="pothole.locationAccuracy.seeMore"
          >
            <Icon icon={showMore ? 'chevronUp' : 'chevronDown'} className="size-4" />
            {showMore ? 'See less' : 'See more'}
          </button>
        </>
      )}
    </FormSection>
  );
}

export function ManholeDialogContent({ geoItem, closeDialog }: { geoItem: GeoItem; closeDialog: () => void }) {
  const { projectId } = useParams({ strict: false }) as ProjectDetailsParams;
  const form = useForm<TManholeFormValues>({
    defaultValues: getInitialValues({ geoItem, fieldMap: manholeFieldMap }),
    mode: 'all',
  });

  const refetch = useInvalidateGeoItems({ projectId });
  const baseVersion = useEditBaseVersion(geoItem);

  const deleteItem = useDeleteGeoItem({
    projectId,
    geoItemId: geoItem.id,
    baseVersion,
    onSuccess: () => {
      Notify.info({ message: 'Manhole log deleted' });
      refetch();
      closeDialog();
    },
  });

  const mutation = useUpdateGeoItem({
    projectId,
    geoItemId: geoItem.id,
    baseVersion,
    onSuccess: () => {
      Notify.info({ message: 'Manhole log updated' });
      form.reset({}, { keepValues: true });
      refetch();
      closeDialog();
    },
  });

  const onSubmit: SubmitHandler<TManholeFormValues> = (data) => {
    const allPropLabels = Object.keys(manholeFieldConfig);

    const props = allPropLabels.map((p) => {
      const formField = manholeMappings[p] as keyof TManholeFormValues | undefined;
      if (!!formField && data[formField] !== undefined) {
        return {
          label: p ?? '',
          value: data[formField] ?? '',
          type: manholeFieldConfig[p] ?? 'string',
        };
      } else {
        return (
          geoItem.fields?.[p] ?? {
            label: p,
            value: '',
            type: manholeFieldConfig[p] ?? 'string',
          }
        );
      }
    });

    mutation.mutate(props);
  };

  const isDisabled =
    !form.formState.isDirty || mutation.isPending || mutation.isError || deleteItem.isPending || deleteItem.isError;

  return (
    <Form onSubmit={form.handleSubmit(onSubmit)} className="flex w-[75vw] max-w-[min(100%,72rem)] flex-col text-sm">
      <div
        className={classNames('grid gap-2', {
          'grid-cols-1': !geoItem.files.length,
          'grid-cols-2': !!geoItem.files.length,
        })}
      >
        <div className="space-y-4">
          <FormSection title="General">
            <ControlledInput control={form.control} label="MH#" name="manholeNumber" isRequired />

            <FormSelect
              control={form.control}
              name="siteChoice"
              label="Site Choice"
              options={siteChoiceItems}
              data-testid="field-note-site-choice-select"
            />

            <ControlledInput control={form.control} label="Crew Members" name="crewMembers" />

            <FormSelect
              control={form.control}
              name="weatherConditions"
              label="Weather Conditions"
              options={weatherConditionItems}
              data-testid="field-note-weather-conditions-select"
            />

            <ControlledInput control={form.control} name="addressLocation" label="Address/Location" />
          </FormSection>

          <FormSection title="Hydraulic Conditions (Influent)">
            <ControlledInput control={form.control} name="velocity" label="Velocity (ft/s)" />

            <ControlledInput control={form.control} name="depth" label="Depth" />
          </FormSection>

          <FormSection title="Sewer Line Characteristics">
            <ControlledInput control={form.control} name="heightInf1" label="Height (Inf1)" />

            <ControlledInput control={form.control} name="widthInf1" label="Width (Inf1)" />

            <ControlledInput control={form.control} name="materialInf1" label="Material (Inf1)" />

            <ControlledInput control={form.control} name="shapeInf1" label="Shape (Inf1)" />

            <ControlledInput control={form.control} name="heightInf2" label="Height (Inf2)" />

            <ControlledInput control={form.control} name="widthInf2" label="Width (Inf2)" />

            <ControlledInput control={form.control} name="materialInf2" label="Material (Inf2)" />

            <ControlledInput control={form.control} name="shapeInf2" label="Shape (Inf2)" />

            <ControlledInput control={form.control} name="heightEff" label="Height (Eff)" />

            <ControlledInput control={form.control} name="widthEff" label="Width (Eff)" />

            <ControlledInput control={form.control} name="materialEff" label="Material (Eff)" />

            <ControlledInput control={form.control} name="shapeEff" label="Shape (Eff)" />
          </FormSection>

          <FormSection title="Sediment">
            <FormSelect
              control={form.control}
              name="sedimentPresent"
              label="Sediment Present"
              options={yesNoItems}
              data-testid="field-note-sediment-present-select"
            />

            <ControlledInput control={form.control} name="hardPacked" label="Hard Packed" />

            <ControlledInput control={form.control} name="softPacked" label="Soft Packed" />
          </FormSection>

          <FormSection title="Surcharge/Backwater">
            <FormSelect
              control={form.control}
              name="surcharge"
              label="Surcharge"
              options={surchargeItems}
              data-testid="field-note-surcharge-select"
            />

            <ControlledInput control={form.control} name="distanceFromRim" label="Distance From Rim (ft)" />
          </FormSection>

          <FormSection title="Gas Investigation">
            <ControlledInput control={form.control} name="O2level" label="O2 Level (%)" />
          </FormSection>

          <FormSection title="Site Conditions">
            <FormSelect
              control={form.control}
              name="siteAccess"
              label="Site Access"
              options={siteAccessItems}
              data-testid="field-note-site-access-select"
            />
          </FormSection>

          <FormSection title="Manhole Information">
            <FormSelect
              control={form.control}
              name="elevatedManhole"
              label="Elevated Manhole"
              options={yesNoItems}
              data-testid="field-note-elevated-manhole-select"
            />

            <FormSelect
              control={form.control}
              name="boltedManhole"
              label="Bolted Manhole"
              options={yesNoItems}
              data-testid="field-note-bolted-manhole-select"
            />

            <ControlledInput control={form.control} name="heightAboveGround" label="Height Above Ground (ft)" />

            <ControlledInput control={form.control} name="manholeDepth" label="Manhole Depth (ft)" />

            <ControlledInput control={form.control} name="pipeOffset" label="Pipe Offset (ft)" />

            <FormSelect
              control={form.control}
              name="structureIntegrity"
              label="Structural Integrity"
              options={structuralIntegrityItems}
              data-testid="field-note-structure-integrity-select"
            />
          </FormSection>

          <FormSection title="Pipe Condition">
            <FormSelect
              control={form.control}
              name="pipeBends"
              label="Pipe Bends"
              options={infEffItems}
              data-testid="field-note-pipe-bends-select"
            />

            <ControlledInput control={form.control} name="distanceToBend" label="Distance to Bend (ft)" />

            <FormSelect
              control={form.control}
              name="sizeGeomMaterialChange"
              label="Size/Geom/Material Change"
              options={infEffItems}
              data-testid="field-note-size-geom-material-change-select"
            />

            <ControlledInput control={form.control} name="distanceToChange" label="Distance to Change (ft)" />

            <ControlledInput control={form.control} name="changeDetails" label="Change Details" />
          </FormSection>

          <FormSection title="Crew Member Observations">
            <FormSelect
              control={form.control}
              name="canMaintainSite"
              label="Can Maintain Site?"
              options={yesNoMaybeItems}
              data-testid="field-note-can-maintain-site-select"
            />

            <ControlledInput control={form.control} name="siteComments" label="Site Comments" />
          </FormSection>

          <FormSection title="Sensor Configuration">
            <ControlledInput control={form.control} name="levelPrimary" label="Level (Primary)" />

            <ControlledInput control={form.control} name="levelRedundant" label="Level (Redundant)" />

            <ControlledInput control={form.control} name="depthPrimary" label="Depth (Primary)" />

            <ControlledInput control={form.control} name="depthRedundant" label="Depth (Redundant)" />

            <ControlledInput control={form.control} name="installComments" label="Install Comments" />
          </FormSection>
        </div>

        {!!geoItem.files.length && (
          <div>
            <Label>Files</Label>

            <FileList geoItem={geoItem} />
          </div>
        )}
      </div>

      <div className="sticky -bottom-4 mt-4 flex items-center justify-end gap-2 bg-white pb-4 pt-4">
        <Button
          type="button"
          variant="danger"
          data-testid="potholing-manhole-delete-button"
          disabled={deleteItem.isPending || deleteItem.isError || mutation.isPending || mutation.isError}
          loading={deleteItem.isPending}
          onClick={() => {
            const result = window.confirm('Are you sure you want to delete this manhole log?');

            if (result) {
              deleteItem.mutate();
            }
          }}
        >
          Delete
        </Button>

        <Button
          type="submit"
          disabled={isDisabled}
          loading={mutation.isPending}
          data-testid="potholing-manhole-save-button"
        >
          Save
        </Button>
      </div>
    </Form>
  );
}

export function PotholeDialogContent({ geoItem, closeDialog }: { geoItem: GeoItem; closeDialog: () => void }) {
  const { projectId } = useParams({ strict: false }) as ProjectDetailsParams;
  const form = useForm<TPotholeFormValues>({
    defaultValues: getInitialValues({ geoItem, fieldMap: potholeFieldMap }),
  });

  const refetch = useInvalidateGeoItems({ projectId });
  const baseVersion = useEditBaseVersion(geoItem);

  const deleteItem = useDeleteGeoItem({
    projectId,
    geoItemId: geoItem.id,
    baseVersion,
    onSuccess: () => {
      Notify.info({ message: 'Pothole log deleted.' });
      refetch();
      closeDialog();
    },
  });

  const updatePothole = useUpdateGeoItem({
    projectId,
    geoItemId: geoItem.id,
    baseVersion,
    onSuccess: () => {
      Notify.info({ message: 'Pothole log updated' });
      form.reset({}, { keepValues: true });
      refetch();
      closeDialog();
    },
  });

  // Per-record gate for the per-utility measurements UI + write path. The new
  // structured behavior is used when EITHER the feature flag is on OR this record
  // already carries a structured `Utility Measurements` prop (authored by a new
  // client) — opening such multi-utility data in the single-input legacy form
  // would silently drop the 2nd+ utilities. While the flag is off, legacy records
  // stay on the legacy editor so web never originates the structured prop.
  const perUtilityEnabled = usePerUtilityMeasurementsEnabled();
  const recordHasStructured = getUtilityMeasurementEntries(geoItem.props).length > 0;
  const useStructured = perUtilityEnabled || recordHasStructured;

  const onSubmit: SubmitHandler<TPotholeFormValues> = (data) => {
    updatePothole.mutate(buildPotholeUpdateProps({ data, geoItem, useStructured }) as any);
  };

  const checkedTargetUtilities = form.watch('targetUtility');
  const checkedUtilities = form.watch('utility') ?? [];
  const utilityMeasurements = form.watch('utilityMeasurements') ?? [];
  const phNumberClash = usePhNumberClash({
    projectId,
    geoItemId: geoItem.id,
    initialPhNumber: form.formState.defaultValues?.potholeNumber,
    phNumber: form.watch('potholeNumber'),
  });
  // Re-validates PH# as it changes, so a clash shows before Save rather than on it.
  useEffect(() => {
    if (phNumberClash || form.getFieldState('potholeNumber', form.formState).error) form.trigger('potholeNumber');
  }, [phNumberClash, form]);
  const phNumberSuggestion = phNumberClash?.suggestion;

  // Survey Results is a legacy section being phased out: hidden on new potholes,
  // surfaced (collapsed) only when an existing record already has values.
  // Computed once from the initial values so it doesn't vanish mid-edit.
  const [showSurveyResults] = useState(() =>
    SURVEY_RESULT_FIELDS.some((name) => String(form.getValues(name) ?? '').trim() !== ''),
  );

  // Keep the legacy `utility` prop in sync as the deduped set of utility *types*
  // present (old clients read this list; duplicates are meaningless there).
  const syncUtilitySelection = (entries: UtilityMeasurementEntry[]) => {
    form.setValue('utilityMeasurements', entries, { shouldDirty: true });
    form.setValue('utility', Array.from(new Set(entries.map((e) => e.utility))), { shouldDirty: true });
  };

  // Add a new measurement block. Duplicate types are allowed — adding "Gas"
  // twice yields two independent blocks.
  const addUtility = (utility: string) => {
    syncUtilitySelection([...(form.getValues('utilityMeasurements') ?? []), { utility }]);
  };

  // Remove the block at `index`. Prompts first when that block still holds data.
  const removeUtilityAt = (index: number) => {
    const entries = form.getValues('utilityMeasurements') ?? [];
    const entry = entries[index];
    const hasData =
      !!entry && Object.entries(entry).some(([k, v]) => k !== 'utility' && typeof v === 'string' && v.trim() !== '');
    if (hasData && !window.confirm(`Remove the measurements recorded for ${entry.utility}?`)) {
      return;
    }
    syncUtilitySelection(entries.filter((_, i) => i !== index));
  };

  return (
    <Form onSubmit={form.handleSubmit(onSubmit)} className="flex w-[75vw] max-w-[min(100%,72rem)] flex-col text-sm">
      <div
        className={classNames('grid gap-2', {
          'grid-cols-1': !geoItem.files.length,
          'grid-cols-2': !!geoItem.files.length,
        })}
      >
        <div className="space-y-4">
          <FormSection title="General">
            <ControlledInput
              control={form.control}
              name="potholeNumber"
              label="PH#"
              isRequired
              validate={{ phNumberFree: () => !phNumberClash || PH_NUMBER_TAKEN }}
              data-testid="pothole-ph-number"
              endAction={
                phNumberSuggestion && (
                  <PhNumberAutoNumberButton
                    suggestion={phNumberSuggestion}
                    onUse={(n) => form.setValue('potholeNumber', n, { shouldDirty: true, shouldValidate: true })}
                  />
                )
              }
            />

            <FormSelect
              control={form.control}
              name="priority"
              label="Priority"
              options={priorityItems}
              data-testid="field-note-priority-select"
            />

            <ControlledInput control={form.control} name="holeDepth" label="Hole Depth (in)" />
          </FormSection>

          <FormSection title="Surface & Soil">
            <FormSelect
              control={form.control}
              name="surfaceType"
              label="Surface Type"
              options={surfaceTypeItems}
              data-testid="field-note-surface-type-select"
            />

            <ControlledInput control={form.control} name="surfaceThickness" label="Surface Thickness (in)" />

            <ControlledInput control={form.control} name="baseThickness" label="Base Thickness (in)" />

            <FormSelect
              control={form.control}
              name="soil"
              label="Soil"
              options={soilItems}
              data-testid="field-note-soil-select"
            />
          </FormSection>

          <FormSection title="Utilities Present">
            {useStructured ? (
              <>
                {/* One card per utility with its own depth/material/owner fields.
                      Add utilities via the dropdown below; each card can be removed
                      independently. On save, cards persist as the structured
                      `utilityMeasurements` prop and entry[0] is dual-written to the
                      legacy scalar props for older clients. */}
                {utilityMeasurements.length === 0 ? (
                  <div className="text-sm text-gray-500">Add a utility to record per-utility depths and materials.</div>
                ) : (
                  utilityMeasurements.map((_, i) => (
                    <UtilityMeasurementCard
                      // biome-ignore lint/suspicious/noArrayIndexKey: index is the block identity — duplicate utility types are allowed, so there is no stable per-entry id
                      key={i}
                      control={form.control}
                      index={i}
                      displayLabel={utilityDisplayLabel(utilityMeasurements, i)}
                      onDelete={() => removeUtilityAt(i)}
                    />
                  ))
                )}

                {/* Add a found utility one at a time. The button toggles a
                      selection menu the user then taps to pick a utility (per
                      design). Duplicate types are allowed, so the full type list is
                      always offered. */}
                <AddUtilityControl utilityOptions={utilityTypes} onAdd={addUtility} />
              </>
            ) : (
              <>
                {/* Legacy single-measurement editor. Rendered while the per-utility
                      feature flag is off AND this record has no structured prop, so
                      web never originates the structured `utilityMeasurements` shape
                      that older mobile clients can't read. */}
                <div>
                  <div>Utility</div>
                  <div className="grid grid-cols-[repeat(auto-fill,minmax(120px,1fr))] gap-2">
                    {utilityTypes.map((t) => {
                      const values = checkedUtilities;
                      const isChecked = values.includes(t);

                      return (
                        <Checkbox
                          key={`utility-${t}`}
                          label={t}
                          checked={isChecked}
                          value={isChecked}
                          onChange={(v) => {
                            if (v) {
                              const copy = [...values, t];
                              form.setValue('utility', Array.from(new Set(copy)), { shouldDirty: true });
                            } else {
                              const copy = [...values.filter((v) => v !== t)];
                              form.setValue('utility', Array.from(new Set(copy)), { shouldDirty: true });
                            }
                          }}
                        />
                      );
                    })}
                  </div>
                </div>

                <FormSelect
                  control={form.control}
                  name="material"
                  label="Material"
                  options={materialItems}
                  data-testid="field-note-material-select"
                />
                <ControlledInput control={form.control} name="depthToTop" label="Depth to Top" />
                <ControlledInput control={form.control} name="depthToBottom" label="Depth to Bottom" />
                <ControlledInput control={form.control} name="depth" label="Depth" />
                <ControlledInput control={form.control} name="utilityOwner" label="Utility Owner" />
              </>
            )}

            {/* Separate Target Utility from the found-utility group above
                  so the grouping reads clearly. Shown in both editors. */}
            <div className="mt-6">
              <div>Target Utility</div>
              <div className="mt-2 grid grid-cols-[repeat(auto-fill,minmax(120px,1fr))] gap-2">
                {utilityTypes.map((t) => {
                  const values = checkedTargetUtilities;
                  const isChecked = values.includes(t);

                  return (
                    <Checkbox
                      key={`utility-${t}`}
                      label={t}
                      checked={isChecked}
                      value={isChecked}
                      onChange={(v) => {
                        if (v) {
                          const copy = [...values, t];
                          form.setValue('targetUtility', Array.from(new Set(copy)), { shouldDirty: true });
                        } else {
                          const copy = [...values.filter((v) => v !== t)];
                          form.setValue('targetUtility', Array.from(new Set(copy)), { shouldDirty: true });
                        }
                      }}
                    />
                  );
                })}
              </div>
            </div>
          </FormSection>

          {showSurveyResults && (
            <CollapsibleCard title="Survey Results" defaultCollapsed testId="pothole.surveyResults">
              <ControlledInput control={form.control} name="northing" label="Northing" />

              <ControlledInput control={form.control} name="easting" label="Easting" />

              <ControlledInput control={form.control} name="elevation" label="Elevation" />

              <ControlledInput control={form.control} name="longitudeLatitude" label={LONGITUDE_LATITUDE_PROP_LABEL} />

              <ControlledInput control={form.control} name="surveyDate" label="Survey Date" />

              <ControlledInput control={form.control} name="surveyedBy" label="Surveyed By" />
            </CollapsibleCard>
          )}

          <FormSection title="Notes">
            <ControlledInput control={form.control} name="location" label="Location" />

            <ControlledInput control={form.control} name="notes" label="Notes" />

            <ControlledInput control={form.control} name="address" label="Address" />
          </FormSection>

          <LocationAccuracySection geoItem={geoItem} />
        </div>

        {!!geoItem.files.length && (
          <div>
            <Label>Files</Label>

            <FileList geoItem={geoItem} />
          </div>
        )}
      </div>

      <div className="sticky -bottom-4 mt-4 flex items-center justify-end gap-2 bg-white pb-4 pt-4">
        <Button
          type="button"
          variant="danger"
          data-testid="potholing-pothole-delete-button"
          disabled={deleteItem.isPending || deleteItem.isError || updatePothole.isPending || updatePothole.isError}
          loading={deleteItem.isPending}
          onClick={() => {
            const result = window.confirm('Are you sure you want to delete this pothole log?');

            if (result) {
              deleteItem.mutate();
            }
          }}
        >
          Delete
        </Button>

        <Button
          type="submit"
          data-testid="potholing-pothole-save-button"
          loading={updatePothole.isPending}
          disabled={
            !form.formState.isDirty ||
            updatePothole.isPending ||
            updatePothole.isError ||
            deleteItem.isPending ||
            deleteItem.isError
          }
        >
          Save
        </Button>
      </div>
    </Form>
  );
}
